import React from 'react';
import { BookOpen, CreditCard, Globe2 } from 'lucide-react';
import { ArrowUpRightIcon } from '@heroicons/react/24/outline';
import { audienceProposalCardKey, type AssistantDisplayPayload } from '@charitystack/dashboard-assistant-contracts';
import type { DashboardAssistantDockMessage } from '../../../../contexts/DashboardAssistantDockContext';
import type {
  AudienceProposalDisplayPayload,
  ContactSelectionPreviewDisplayPayload,
  ContactTagsApplyResultDisplayPayload,
  DashboardAnalyticsDisplayPayload,
  DashboardAnalyticsResultPayload,
  DashboardMessageRenderItem,
  DashboardSearchDisplayPayload,
  DashboardSearchResultPayload,
  DocumentationSourcePayload,
  DocumentationSourcesDisplayPayload,
  SuggestedNavigationPayload,
} from '../types';
import { getDashboardPathLabel } from './dashboardPathLabels';
import { AUDIENCE_PROPOSAL_OUTCOME_SOURCE } from '../../../../pages/marketing/nativeAudienceProposalOutcome';
import {
  parseDashboardCompactSearchToolResult,
  parseDashboardContextResult,
  parseDashboardDonationSummaryToolResult,
  parseDashboardNavigationResult,
  parseDashboardSearchResult,
  parseDashboardToolError,
} from './toolResultParsers';
import { getDockMessageKey } from './messageModel';

export function isSuggestedNavigationPayload(
  payload: AssistantDisplayPayload | undefined,
): payload is SuggestedNavigationPayload {
  return (
    payload?.renderer === 'suggested_navigation' || payload?.renderer === 'dashboard_navigation'
  );
}

export function isDashboardSearchDisplayPayload(
  payload: AssistantDisplayPayload | undefined,
): payload is DashboardSearchDisplayPayload {
  return payload?.renderer === 'dashboard_search_results' && Boolean(payload.data);
}

export function isDashboardAnalyticsDisplayPayload(
  payload: AssistantDisplayPayload | undefined,
): payload is DashboardAnalyticsDisplayPayload {
  return payload?.renderer === 'dashboard_analytics_result' && Boolean(payload.data);
}

export function isContactSelectionPreviewPayload(
  payload: AssistantDisplayPayload | undefined,
): payload is ContactSelectionPreviewDisplayPayload {
  return payload?.renderer === 'contact_selection_preview' && Boolean(payload.data);
}

export function isContactTagsApplyResultPayload(
  payload: AssistantDisplayPayload | undefined,
): payload is ContactTagsApplyResultDisplayPayload {
  return payload?.renderer === 'contact_tags_apply_result' && Boolean(payload.data);
}

export function isAudienceProposalPayload(
  payload: AssistantDisplayPayload | undefined,
): payload is AudienceProposalDisplayPayload {
  return payload?.renderer === 'audience_proposal' && Boolean(payload.data);
}

export function isDocumentationSourcesPayload(
  payload: AssistantDisplayPayload | undefined,
): payload is DocumentationSourcesDisplayPayload {
  return payload?.renderer === 'documentation_sources' && Boolean(payload.data);
}

export function getSafeDocumentationSources(
  payload: DocumentationSourcesDisplayPayload,
): DocumentationSourcePayload[] {
  const sources = Array.isArray(payload.data.sources) ? payload.data.sources : [];
  return sources.filter((source) => {
    if (!source || typeof source.title !== 'string' || typeof source.url !== 'string') {
      return false;
    }
    try {
      const url = new URL(source.url);
      return (
        url.origin === 'https://feedback.charitystack.com' &&
        (url.pathname === '/docs' || url.pathname.startsWith('/docs/'))
      );
    } catch {
      return false;
    }
  });
}

export function getDocumentationSourceUrls(messages: DashboardAssistantDockMessage[]) {
  return messages.flatMap((message) =>
    isDocumentationSourcesPayload(message.displayPayload)
      ? getSafeDocumentationSources(message.displayPayload).map((source) => source.url)
      : [],
  );
}

function isRichDisplayPayload(payload: AssistantDisplayPayload | undefined) {
  return (
    isDashboardSearchDisplayPayload(payload) ||
    isDashboardAnalyticsDisplayPayload(payload) ||
    isContactSelectionPreviewPayload(payload) ||
    isContactTagsApplyResultPayload(payload) ||
    isAudienceProposalPayload(payload)
  );
}

export function getDashboardSearchActivitySummary(payload: DashboardSearchResultPayload) {
  const results = payload.results || {};
  const count =
    (results.contacts?.length || 0) +
    (results.recentPayments?.length || 0) +
    (results.subscriptions?.length || 0) +
    (results.forms?.length || 0) +
    (results.elements?.length || 0);

  return {
    count,
    label: `Searched dashboard for "${payload.query || 'records'}"`,
    meta:
      count === 0
        ? 'No matching records'
        : `${count} result${count === 1 ? '' : 's'}${payload.organizationName ? ` in ${payload.organizationName}` : ''}`,
  };
}

export function getDashboardAnalyticsActivitySummary(payload: DashboardAnalyticsResultPayload) {
  const rowCount = payload.rows?.length || 0;
  return {
    count: rowCount,
    label: payload.title || 'Generated dashboard analytics',
    meta:
      rowCount === 0
        ? 'No matching analytics rows'
        : `${rowCount} row${rowCount === 1 ? '' : 's'}${payload.query?.dateRangeLabel ? ` · ${payload.query.dateRangeLabel}` : ''}`,
  };
}

export function getActivityMessageDetails(message: DashboardAssistantDockMessage) {
  if (isDocumentationSourcesPayload(message.displayPayload)) {
    const sources = getSafeDocumentationSources(message.displayPayload);
    const status = message.displayPayload.data.status;
    return {
      icon: <BookOpen className="h-3.5 w-3.5" />,
      label:
        status === 'error'
          ? 'Documentation search unavailable'
          : message.displayPayload.summary || 'Searched CharityStack docs',
      meta:
        status === 'error'
          ? 'Could not check the help center'
          : sources.length === 0
            ? 'No matching sources'
            : `${sources.length} source${sources.length === 1 ? '' : 's'}`,
    };
  }

  if (isDashboardAnalyticsDisplayPayload(message.displayPayload)) {
    const summary = getDashboardAnalyticsActivitySummary(message.displayPayload.data);
    return {
      icon: <CreditCard className="h-3.5 w-3.5" />,
      label: summary.label,
      meta: summary.meta,
    };
  }

  if (isDashboardSearchDisplayPayload(message.displayPayload)) {
    const summary = getDashboardSearchActivitySummary(message.displayPayload.data);
    return {
      icon: <Globe2 className="h-3.5 w-3.5" />,
      label: summary.label,
      meta: summary.meta,
    };
  }

  if (isSuggestedNavigationPayload(message.displayPayload)) {
    const path =
      typeof message.displayPayload.data.path === 'string' ? message.displayPayload.data.path : '';
    return {
      icon: <ArrowUpRightIcon className="h-3.5 w-3.5" />,
      label:
        message.displayPayload.summary ||
        `${message.displayPayload.renderer === 'dashboard_navigation' ? 'Opened' : 'Suggested'} ${getDashboardPathLabel(path) || path || 'dashboard page'}`,
      meta: path,
    };
  }

  const searchResult = parseDashboardSearchResult(message.content);
  if (searchResult) {
    const summary = getDashboardSearchActivitySummary(searchResult);
    return {
      icon: <Globe2 className="h-3.5 w-3.5" />,
      label: summary.label,
      meta: summary.meta,
    };
  }

  const compactSearchResult = parseDashboardCompactSearchToolResult(message.content);
  if (compactSearchResult) {
    const resultCount = compactSearchResult.resultCount || 0;
    return {
      icon: <Globe2 className="h-3.5 w-3.5" />,
      label: `Searched dashboard for "${compactSearchResult.query}"`,
      meta:
        resultCount === 0
          ? 'No matching records'
          : `${resultCount} result${resultCount === 1 ? '' : 's'}${compactSearchResult.organizationName ? ` in ${compactSearchResult.organizationName}` : ''}`,
    };
  }

  const navigationResult = parseDashboardNavigationResult(message.content);
  if (navigationResult?.path) {
    return {
      icon: <ArrowUpRightIcon className="h-3.5 w-3.5" />,
      label: `Opened ${getDashboardPathLabel(navigationResult.path) || 'dashboard page'}`,
      meta: navigationResult.path,
    };
  }

  const contextResult = parseDashboardContextResult(message.content);
  if (contextResult) {
    return {
      icon: <Globe2 className="h-3.5 w-3.5" />,
      label: `Checked current page${contextResult.currentFeature ? `: ${contextResult.currentFeature}` : ''}`,
      meta: contextResult.organizationName || contextResult.route?.pathname,
    };
  }

  const toolError = parseDashboardToolError(message.content);
  if (toolError?.message) {
    return {
      icon: <Globe2 className="h-3.5 w-3.5" />,
      label: toolError.message,
      meta: 'Tool result',
    };
  }

  const donationSummary = parseDashboardDonationSummaryToolResult(message.content);
  if (donationSummary) {
    const contact =
      donationSummary.contact?.name ||
      donationSummary.contact?.email ||
      donationSummary.contact?.contactId ||
      donationSummary.contactId ||
      'contact';
    const count = Number(donationSummary.donationSummary?.donationCount || 0);
    return {
      icon: <CreditCard className="h-3.5 w-3.5" />,
      label: `Checked donation summary for ${contact}`,
      meta:
        donationSummary.status === 'no_donations'
          ? 'No completed donations'
          : `${count} donation${count === 1 ? '' : 's'}${donationSummary.organizationName ? ` in ${donationSummary.organizationName}` : ''}`,
    };
  }

  return {
    icon: <Globe2 className="h-3.5 w-3.5" />,
    label: message.content || message.displayPayload?.summary || 'Used a dashboard tool',
    meta: message.metadata?.toolName as string | undefined,
  };
}

export function isDashboardActivityMessage(message: DashboardAssistantDockMessage) {
  if (message.role === 'user') {
    return false;
  }

  if (isRichDisplayPayload(message.displayPayload)) {
    return false;
  }

  return (
    message.kind === 'action' ||
    Boolean(message.displayPayload) ||
    Boolean(
      parseDashboardSearchResult(message.content) ||
        parseDashboardCompactSearchToolResult(message.content) ||
        parseDashboardNavigationResult(message.content) ||
        parseDashboardContextResult(message.content) ||
        parseDashboardToolError(message.content) ||
        parseDashboardDonationSummaryToolResult(message.content),
    )
  );
}

// A segment proposal card records its save outcome as an action entry, so any
// browser that opens the chat can show "View segment" (F-23). It is the card's
// state, never a line in the conversation.
export { AUDIENCE_PROPOSAL_OUTCOME_SOURCE };

export function isAudienceProposalOutcomeMessage(message: DashboardAssistantDockMessage) {
  return message.kind === 'action' && message.metadata?.source === AUDIENCE_PROPOSAL_OUTCOME_SOURCE;
}

// "Use this list" names no list: the assistant drafts from the chat's newest
// selection only. So only the newest selection card offers it, when it is a
// segment's list and no proposal has been drafted from it yet (ADM-197).
export function getUseListItemId(items: DashboardMessageRenderItem[]): string | null {
  for (let index = items.length - 1; index >= 0; index -= 1) {
    const item = items[index];
    if (item.type !== 'message') continue;
    const payload = item.message.displayPayload;
    if (isAudienceProposalPayload(payload)) return null;
    if (isContactSelectionPreviewPayload(payload)) {
      return payload.data.purpose === 'segment' ? item.id : null;
    }
  }
  return null;
}

// True while the newest segment card still waits for its Use this list button:
// the card offers it and the user has not replied since. Chat suggestion chips
// stay hidden then, because a chip sends plain text and skips the button's
// save of pending unchecks (prod test 2026-10-14, C1; QA R3-02).
export function hasPendingUseListCard(items: DashboardMessageRenderItem[]): boolean {
  const useListItemId = getUseListItemId(items);
  if (!useListItemId) return false;

  const cardIndex = items.findIndex((item) => item.id === useListItemId);
  return !items
    .slice(cardIndex + 1)
    .some((item) => item.type === 'message' && item.message.role === 'user');
}

/**
 * One card per segment per reply (ADM-228): a proposal card that a later card
 * in the same run (or turn) replaces, for the same segment. Only the latest is
 * shown, so an earlier one can't be confirmed. A card with no run or turn id is
 * never replaced.
 */
function replacedProposalMessages(messages: DashboardAssistantDockMessage[]): Set<DashboardAssistantDockMessage> {
  const latest = new Map<string, DashboardAssistantDockMessage>();
  const replaced = new Set<DashboardAssistantDockMessage>();
  for (const message of messages) {
    const payload = message.displayPayload;
    const reply = message.runId || message.turnId;
    if (!reply || !isAudienceProposalPayload(payload)) continue;
    const proposal = payload.data?.proposal;
    if (!proposal || typeof proposal.name !== 'string') continue;
    const key = `${reply}|${audienceProposalCardKey(proposal)}`;
    const earlier = latest.get(key);
    if (earlier) replaced.add(earlier);
    latest.set(key, message);
  }
  return replaced;
}

export function groupDashboardMessageRenderItems(
  messages: DashboardAssistantDockMessage[],
): DashboardMessageRenderItem[] {
  const items: DashboardMessageRenderItem[] = [];
  const replaced = replacedProposalMessages(messages);
  let activityMessages: DashboardAssistantDockMessage[] = [];

  const flushActivityMessages = () => {
    if (activityMessages.length === 0) {
      return;
    }

    items.push({
      id: `activity-${activityMessages.map(getDockMessageKey).join('|')}`,
      messages: activityMessages,
      type: 'activity',
    });
    activityMessages = [];
  };

  messages.forEach((message) => {
    if (isAudienceProposalOutcomeMessage(message) || replaced.has(message)) {
      return;
    }

    if (isRichDisplayPayload(message.displayPayload)) {
      flushActivityMessages();
      items.push({
        id: getDockMessageKey(message),
        message,
        type: 'message',
      });
      return;
    }

    if (isDashboardActivityMessage(message)) {
      activityMessages.push(message);
      return;
    }

    flushActivityMessages();
    items.push({
      id: getDockMessageKey(message),
      message,
      type: 'message',
    });
  });

  flushActivityMessages();
  return items;
}
